import React, { useEffect, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import { Trial, Product, Recipe, RecipeIngredient, Ingredient, TrialIngredient } from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import NumberInput from "../../../components/DataEntry/NumberInput";
import { bakerPctNum, getTrialId, getTrialName } from "./trialUtils";
import { calcFlatMetrics, RecipeMetrics } from "../../../utils/recipeMetrics";
import { t } from "../../../i18n/locale";

type CreationMode = "scratch" | "recipe" | "trial";

interface IngredientEntry {
  ingredient_id: number | null;
  ingredient_name: string;
  ingredient_type: string;
  baker_percentage: number;
  is_new_ingredient: boolean;
  notes: string;
}

/** Puna stranica za kreiranje nove probe (umjesto modala) */
const TrialCreatePage = () => {
  savePathToLocalStorage();
  const history = useHistory();

  const [trials, setTrials] = useState<Trial[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Creation mode
  const [creationMode, setCreationMode] = useState<CreationMode | null>(null);
  const [step, setStep] = useState(1);

  // Source selection for modes
  const [sourceTrialId, setSourceTrialId] = useState<number | null>(null);
  const [sourceRecipeId, setSourceRecipeId] = useState<number | null>(null);

  // Trial form state
  const [newTrial, setNewTrial] = useState({
    trial_name: "",
    product_id: 0,
    product_name_custom: "",
    trial_date: new Date().toISOString().split("T")[0],
    dough_mass_kg: "",
    purpose: "",
    notes: "",
  });

  // Ingredients for recipe builder
  const [trialIngredients, setTrialIngredients] = useState<IngredientEntry[]>([]);

  // Baseline metrics of the originally loaded recipe/trial (for variance/delta display)
  const [baselineMetrics, setBaselineMetrics] = useState<RecipeMetrics | null>(null);
  const [baselineLabel, setBaselineLabel] = useState<string>("");

  const metricsFromEntries = (entries: IngredientEntry[]): RecipeMetrics =>
    calcFlatMetrics(
      entries.map((i) => ({ type: i.ingredient_type, name: i.ingredient_name, pct: i.baker_percentage }))
    );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [trialsData, productsData, ingredientsData] = await Promise.all([
        mesApi.getTrials({}),
        mesApi.getProducts(),
        mesApi.getIngredients(),
      ]);
      setTrials(trialsData);
      setProducts(productsData);
      setIngredients(ingredientsData);
    } catch (err: any) {
      toast.error("Failed to load data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadRecipesForProduct = async (productId: number) => {
    if (!productId) {
      setRecipes([]);
      return;
    }
    try {
      const data = await mesApi.getRecipes({ product_id: productId });
      setRecipes(data);
    } catch (err: any) {
      console.error("Failed to load recipes:", err);
    }
  };

  const loadRecipeIngredients = async (recipeId: number) => {
    try {
      const recipe = await mesApi.getRecipe(recipeId);
      if (recipe.RecipeIngredient_RecipeToRecipeIngredient_Recipe) {
        const entries: IngredientEntry[] = recipe.RecipeIngredient_RecipeToRecipeIngredient_Recipe.map((ri) => ({
          ingredient_id: resolveIngredientId(
            ri.Ingredient ?? null,
            ri.Ingredient_IngredientToRecipeIngredient?.IngredientName
          ),
          ingredient_name: ri.Ingredient_IngredientToRecipeIngredient?.IngredientName || getIngredientName(ri.Ingredient ?? 0),
          ingredient_type: ri.Ingredient_IngredientToRecipeIngredient?.IngredientTypeName || "",
          baker_percentage: bakerPctNum(ri.BakerPercentage),
          is_new_ingredient: false,
          notes: ri.Notes || "",
        }));
        setTrialIngredients(entries);
        setBaselineMetrics(metricsFromEntries(entries));
        setBaselineLabel(`recept ${recipe.RecipeCode || `#${recipeId}`}`);
      }
    } catch (err: any) {
      toast.error("Failed to load recipe ingredients: " + err.message);
    }
  };

  const loadTrialIngredients = async (trialId: number) => {
    try {
      const ing = await mesApi.getTrialIngredients(trialId);
      const entries: IngredientEntry[] = ing.map((ti) => ({
        ingredient_id: resolveIngredientId(ti.Ingredient, ti.IngredientName),
        ingredient_name: ti.IngredientName,
        ingredient_type: ti.IngredientType || "",
        baker_percentage: bakerPctNum(ti.BakerPercentage),
        is_new_ingredient: ti.IsNewIngredient || false,
        notes: ti.Notes || "",
      }));
      setTrialIngredients(entries);
      setBaselineMetrics(metricsFromEntries(entries));

      // Also load trial info to pre-fill
      const trial = await mesApi.getTrial(trialId);
      setBaselineLabel(`proba ${trial.TrialCode || `#${trialId}`}`);
      setNewTrial({
        trial_name: trial.TrialCode || "",
        product_id: trial.Product || 0,
        product_name_custom: "",
        trial_date: new Date().toISOString().split("T")[0],
        dough_mass_kg: String(trial.DoughMassKg || ""),
        purpose: trial.Purpose || "",
        notes: "",
      });
      if (trial.Product) {
        loadRecipesForProduct(trial.Product);
      }
    } catch (err: any) {
      toast.error("Failed to load trial: " + err.message);
    }
  };

  const handleProductChange = (productId: number) => {
    setNewTrial({ ...newTrial, product_id: productId });
    loadRecipesForProduct(productId);
  };

  const getProductId = (p: Product) => p.ID;
  const getRecipeId = (r: Recipe) => r.ID;
  const getIngredientId = (i: Ingredient) => i.ID;
  const getRecipeName = (r: Recipe) => r.RecipeCode || `Recipe ${getRecipeId(r)}`;
  const getRecipeVersion = (r: Recipe) => r.RecipeVersion || 1;
  const getIngredientName = (ingredientId: number) => {
    return ingredients.find((i) => getIngredientId(i) === ingredientId)?.IngredientName || "N/A";
  };

  /**
   * Riješi ingredient_id iz kataloga prema nazivu kada FK nije postavljen
   * (npr. sastojci kopirani iz recepta/probe gdje veza na sirovinu nije spremljena).
   */
  const resolveIngredientId = (rawId: number | null | undefined, name?: string | null): number | null => {
    if (rawId) return rawId;
    if (!name) return null;
    const target = name.trim().toLowerCase();
    const match = ingredients.find((i) => (i.IngredientName || "").trim().toLowerCase() === target);
    return match ? getIngredientId(match) : null;
  };

  const resetCreateModal = () => {
    setCreationMode(null);
    setStep(1);
    setSourceTrialId(null);
    setSourceRecipeId(null);
    setNewTrial({
      trial_name: "",
      product_id: 0,
      product_name_custom: "",
      trial_date: new Date().toISOString().split("T")[0],
      dough_mass_kg: "",
      purpose: "",
      notes: "",
    });
    setTrialIngredients([]);
    setRecipes([]);
    setBaselineMetrics(null);
    setBaselineLabel("");
  };

  const handleModeSelect = (mode: CreationMode) => {
    setCreationMode(mode);
    if (mode === "scratch") {
      setStep(2);
      // Add empty ingredient row
      setTrialIngredients([createEmptyIngredient()]);
    }
  };

  const createEmptyIngredient = (): IngredientEntry => ({
    ingredient_id: null,
    ingredient_name: "",
    ingredient_type: "",
    baker_percentage: 0,
    is_new_ingredient: false,
    notes: "",
  });

  const handleSourceSelect = async () => {
    if (creationMode === "recipe" && sourceRecipeId) {
      await loadRecipeIngredients(sourceRecipeId);
      setStep(2);
    } else if (creationMode === "trial" && sourceTrialId) {
      await loadTrialIngredients(sourceTrialId);
      setStep(2);
    }
  };

  const addIngredient = () => {
    setTrialIngredients([...trialIngredients, createEmptyIngredient()]);
  };

  const removeIngredient = (index: number) => {
    setTrialIngredients(trialIngredients.filter((_, i) => i !== index));
  };

  const updateIngredient = (index: number, field: keyof IngredientEntry, value: any) => {
    const updated = [...trialIngredients];
    updated[index] = { ...updated[index], [field]: value };

    // If selecting from dropdown, populate name and type
    if (field === "ingredient_id" && value) {
      const ing = ingredients.find((i) => getIngredientId(i) === Number(value));
      if (ing) {
        updated[index].ingredient_name = ing.IngredientName;
        updated[index].ingredient_type = ing.IngredientTypeName || "";
        updated[index].is_new_ingredient = false;
      }
    }

    setTrialIngredients(updated);
  };

  const toggleNewIngredient = (index: number) => {
    const updated = [...trialIngredients];
    updated[index].is_new_ingredient = !updated[index].is_new_ingredient;
    if (updated[index].is_new_ingredient) {
      updated[index].ingredient_id = null;
    }
    setTrialIngredients(updated);
  };

  // Calculate metrics (uskladeno s mes-pekara logikom — recipeMetrics.ts)
  const getMetrics = () =>
    calcFlatMetrics(
      trialIngredients.map((i) => ({
        type: i.ingredient_type,
        name: i.ingredient_name,
        pct: i.baker_percentage,
      }))
    );
  const getTotalBakerPct = () =>
    trialIngredients.reduce((sum, i) => sum + bakerPctNum(i.baker_percentage), 0);

  const calculateMassKg = (bakerPct: number | string): string => {
    const doughMass = parseFloat(newTrial.dough_mass_kg) || 0;
    const totalPct = getTotalBakerPct();
    const pct = bakerPctNum(bakerPct);
    if (totalPct === 0 || doughMass === 0) return "0";
    return (doughMass * pct / totalPct).toFixed(3);
  };

  const handleCreateTrial = async () => {
    if (!newTrial.trial_name || (!newTrial.product_id && !newTrial.product_name_custom)) {
      toast.error(t("Unesite naziv pokusa i proizvod"));
      return;
    }

    if (!newTrial.purpose.trim()) {
      toast.error(t("Unesite svrhu pokusa (obavezno)"));
      return;
    }

    if (trialIngredients.length === 0) {
      toast.error(t("Dodajte barem jedan sastojak"));
      return;
    }

    // Pravilo: ukupno brašno mora biti točno 100% (±0.5) prije skaliranja/spremanja.
    const metrics = getMetrics();
    if (Math.abs(metrics.flour_total - 100) > 0.5) {
      toast.error(
        t("Ukupno brašno mora biti 100% prije spremanja — trenutno {pct}%", {
          pct: metrics.flour_total.toFixed(1),
        })
      );
      return;
    }

    try {
      const doughMass = parseFloat(newTrial.dough_mass_kg) || getTotalBakerPct();

      const ingredientsPayload = trialIngredients.map((i, idx) => ({
        ingredient_id: i.is_new_ingredient ? null : i.ingredient_id,
        ingredient_name: i.ingredient_name,
        ingredient_type: i.ingredient_type,
        baker_percentage: bakerPctNum(i.baker_percentage),
        mass_kg: parseFloat(calculateMassKg(i.baker_percentage)),
        is_new_ingredient: i.is_new_ingredient,
        sort_order: idx + 1,
        notes: i.notes || null,
      }));

      setSaving(true);
      const created = await mesApi.createTrial({
        trial_code: newTrial.trial_name,
        trial_name: newTrial.trial_name,
        product_id: newTrial.product_id || undefined,
        product_name_custom: newTrial.product_name_custom || undefined,
        trial_date: newTrial.trial_date,
        dough_mass_kg: doughMass,
        purpose: newTrial.purpose || undefined,
        notes: newTrial.notes || undefined,
        status: "draft",
        ingredients: ingredientsPayload,
      } as any);

      toast.success(t("Pokus uspjesno kreiran"));
      history.push(`/mes/trials/${getTrialId(created)}`);
    } catch (err: any) {
      toast.error(t("Greska pri kreiranju: {message}", { message: err.message }));
    } finally {
      setSaving(false);
    }
  };

  const MODE_OPTIONS: { mode: CreationMode; icon: string; title: string; desc: string }[] = [
    { mode: "scratch", icon: "📝", title: "Od nule", desc: "Sami složite recepturu, sastojak po sastojak." },
    { mode: "recipe", icon: "📋", title: "Iz recepta", desc: "Preuzmite sastojke postojećeg recepta proizvoda." },
    { mode: "trial", icon: "🧪", title: "Iz pokusa", desc: "Nastavite od ranije probe i mijenjajte postotke." },
  ];

  const renderModeSelection = () => (
    <div className="creation-mode-selection">
      <div className="mode-heading">
        <h4>{t("Odaberite način kreiranja")}</h4>
        <p>{t("Način određuje s kojom recepturom počinjete — kasnije ju u svakom slučaju možete mijenjati.")}</p>
      </div>

      <div className="mode-cards" role="radiogroup" aria-label={t("Način kreiranja probe")}>
        {MODE_OPTIONS.map(({ mode, icon, title, desc }) => (
          <button
            key={mode}
            type="button"
            role="radio"
            aria-checked={creationMode === mode}
            className={`mode-card ${creationMode === mode ? "selected" : ""}`}
            onClick={() => (mode === "scratch" ? handleModeSelect(mode) : setCreationMode(mode))}
          >
            <span className="mode-icon" aria-hidden="true">{icon}</span>
            <span className="mode-title">{t(title)}</span>
            <span className="mode-desc">{t(desc)}</span>
          </button>
        ))}
      </div>

      {/* Source Selection for Recipe Mode */}
      {creationMode === "recipe" && (
        <div className="mode-source-panel">
          <h5>{t("Odaberite izvorni recept")}</h5>
          <div className="form-grid-2">
            <div className="form-group">
              <label>{t("Proizvod")}</label>
              <select
                value={newTrial.product_id || ""}
                onChange={(e) => handleProductChange(Number(e.target.value))}
              >
                <option value="">{t("-- Odaberi --")}</option>
                {products.map((p) => (
                  <option key={getProductId(p)} value={getProductId(p)}>
                    {p.ProductName}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{t("Recept")}</label>
              <select
                value={sourceRecipeId || ""}
                onChange={(e) => setSourceRecipeId(Number(e.target.value))}
                disabled={!newTrial.product_id || recipes.length === 0}
              >
                <option value="">
                  {!newTrial.product_id
                    ? t("-- Prvo odaberite proizvod --")
                    : recipes.length === 0
                    ? t("-- Nema recepata za proizvod --")
                    : t("-- Odaberi --")}
                </option>
                {recipes.map((r) => {
                  const productName =
                    products.find((p) => getProductId(p) === newTrial.product_id)?.ProductName || "";
                  return (
                    <option key={getRecipeId(r)} value={getRecipeId(r)}>
                      {productName ? `${productName} — ` : ""}
                      {getRecipeName(r)} (v{getRecipeVersion(r)})
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
          <div className="mode-source-actions">
            <button type="button" className="btn-primary" onClick={handleSourceSelect} disabled={!sourceRecipeId}>
              {t("Nastavi →")}
            </button>
          </div>
        </div>
      )}

      {/* Source Selection for Trial Mode */}
      {creationMode === "trial" && (
        <div className="mode-source-panel">
          <h5>{t("Odaberite izvornu probu")}</h5>
          <div className="form-group">
            <label>{t("Proba")}</label>
            <select
              value={sourceTrialId || ""}
              onChange={(e) => setSourceTrialId(Number(e.target.value))}
            >
              <option value="">{trials.length === 0 ? t("-- Nema spremljenih proba --") : t("-- Odaberi --")}</option>
              {trials.map((trial) => (
                <option key={getTrialId(trial)} value={getTrialId(trial)}>
                  {getTrialName(trial)} · #{getTrialId(trial)} · {trial.Purpose || t("bez svrhe")}
                </option>
              ))}
            </select>
          </div>
          <div className="mode-source-actions">
            <button type="button" className="btn-primary" onClick={handleSourceSelect} disabled={!sourceTrialId}>
              {t("Nastavi →")}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderTrialForm = () => (
    <>
      {/* Basic Info */}
      <div className="form-grid-2">
        <div className="form-group">
          <label>{t("Naziv pokusa *")}</label>
          <input
            type="text"
            value={newTrial.trial_name}
            onChange={(e) => setNewTrial({ ...newTrial, trial_name: e.target.value })}
            placeholder={t("npr. Test novog brasna")}
          />
        </div>
        <div className="form-group">
          <label>{t("Datum pokusa")}</label>
          <input
            type="date"
            value={newTrial.trial_date}
            onChange={(e) => setNewTrial({ ...newTrial, trial_date: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>{t("Proizvod")}</label>
          <select
            value={newTrial.product_id || ""}
            onChange={(e) => handleProductChange(Number(e.target.value))}
          >
            <option value="">{t("-- Odaberi ili unesi naziv --")}</option>
            {products.map((p) => (
              <option key={getProductId(p)} value={getProductId(p)}>
                {p.ProductName}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>{t("Ili novi naziv proizvoda")}</label>
          <input
            type="text"
            value={newTrial.product_name_custom}
            onChange={(e) => setNewTrial({ ...newTrial, product_name_custom: e.target.value })}
            placeholder={t("Novi proizvod...")}
            disabled={!!newTrial.product_id}
          />
        </div>
        <div className="form-group">
          <label>{t("Masa zamjesa (kg)")}</label>
          <input
            type="number"
            step="0.1"
            value={newTrial.dough_mass_kg}
            onChange={(e) => setNewTrial({ ...newTrial, dough_mass_kg: e.target.value })}
            placeholder={t("npr. 50")}
          />
        </div>
        <div className="form-group">
          <label>{t("Svrha *")}</label>
          <input
            type="text"
            value={newTrial.purpose}
            onChange={(e) => setNewTrial({ ...newTrial, purpose: e.target.value })}
            placeholder={t("Svrha pokusa...")}
          />
        </div>
      </div>

      {/* Live Metrics + varijanca naspram učitanog recepta/probe */}
      {(() => {
        const m = getMetrics();
        const flourOk = Math.abs(m.flour_total - 100) <= 0.5;
        const renderDelta = (current: number, base?: number) => {
          if (base == null) return null;
          const d = current - base;
          if (Math.abs(d) < 0.05) {
            return <div className="metric-delta" style={{ fontSize: "0.75rem", color: "#888" }}>Δ 0.0%</div>;
          }
          return (
            <div
              className="metric-delta"
              style={{ fontSize: "0.75rem", color: d > 0 ? "#27ae60" : "#e74c3c" }}
            >
              Δ {d > 0 ? "+" : ""}
              {d.toFixed(1)}%
            </div>
          );
        };
        return (
          <div className="trial-metrics" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "1rem", margin: "1rem 0" }}>
            <div className="mes-metric-card">
              <div className="metric-label">{t("Ukupni PEK")}</div>
              <div className="metric-value">{m.total_pct.toFixed(1)}%</div>
              {renderDelta(m.total_pct, baselineMetrics?.total_pct)}
            </div>
            <div className="mes-metric-card">
              <div className="metric-label">{t("Ukupno brašno (=100%)")}</div>
              <div className="metric-value" style={{ color: flourOk ? undefined : "#e74c3c" }}>
                {m.flour_total.toFixed(1)}%
              </div>
              {renderDelta(m.flour_total, baselineMetrics?.flour_total)}
            </div>
            <div className="mes-metric-card">
              <div className="metric-label">{t("Hidracija 1")}</div>
              <div className="metric-value">{m.hydration_phase_1.toFixed(1)}%</div>
              {renderDelta(m.hydration_phase_1, baselineMetrics?.hydration_phase_1)}
            </div>
            <div className="mes-metric-card">
              <div className="metric-label">{t("Hidracija 2")}</div>
              <div className="metric-value">{m.total_hydration.toFixed(1)}%</div>
              {renderDelta(m.total_hydration, baselineMetrics?.total_hydration)}
            </div>
          </div>
        );
      })()}
      {baselineMetrics && (
        <p className="mes-hint" style={{ marginTop: "-0.5rem" }}>
          {t("Δ vrijednosti prikazuju promjenu naspram izvornog izvora ({label}).", { label: baselineLabel })}
        </p>
      )}
      {Math.abs(getMetrics().flour_total - 100) > 0.5 && (
        <p className="mes-hint" style={{ color: "#e74c3c" }}>
          {t("⚠️ Ukupno brašno mora biti 100% (trenutno {pct}%) prije skaliranja i spremanja.", { pct: getMetrics().flour_total.toFixed(1) })}
        </p>
      )}

      {/* Ingredients Table */}
      <h4 style={{ marginTop: "1.5rem", marginBottom: "0.75rem" }}>{t("Receptura")}</h4>
      <div className="mes-table-wrap">
        <table className="mes-table ingredient-edit-table">
        <thead>
          <tr>
            <th style={{ width: "30px" }}>#</th>
            <th>{t("Sirovina")}</th>
            <th style={{ width: "100px" }}>{t("Tip")}</th>
            <th style={{ width: "100px" }}>{t("PEK %")}</th>
            <th style={{ width: "110px" }}>{t("kg zamjes")}</th>
            <th style={{ width: "80px" }}>{t("Nova?")}</th>
            <th style={{ width: "60px" }}></th>
          </tr>
        </thead>
        <tbody>
          {trialIngredients.map((ing, idx) => (
            <tr key={idx}>
              <td>{idx + 1}</td>
              <td>
                {ing.is_new_ingredient ? (
                  <input
                    type="text"
                    value={ing.ingredient_name}
                    onChange={(e) => updateIngredient(idx, "ingredient_name", e.target.value)}
                    placeholder={t("Naziv nove sirovine...")}
                    style={{ width: "100%" }}
                  />
                ) : (
                  (() => {
                    const matched =
                      ing.ingredient_id != null &&
                      ingredients.some((i) => getIngredientId(i) === ing.ingredient_id);
                    const showLoadedFallback = !matched && !!ing.ingredient_name;
                    return (
                      <select
                        value={showLoadedFallback ? "__loaded__" : ing.ingredient_id || ""}
                        onChange={(e) => {
                          if (e.target.value === "__loaded__") return;
                          updateIngredient(idx, "ingredient_id", Number(e.target.value));
                        }}
                        style={{ width: "100%" }}
                      >
                        <option value="">{t("-- Odaberi --")}</option>
                        {showLoadedFallback && (
                          <option value="__loaded__">{t("{name} (izvorno)", { name: ing.ingredient_name })}</option>
                        )}
                        {ingredients.map((i) => (
                          <option key={getIngredientId(i)} value={getIngredientId(i)}>
                            {i.IngredientName}
                          </option>
                        ))}
                      </select>
                    );
                  })()
                )}
              </td>
              <td>
                {ing.is_new_ingredient ? (
                  <input
                    type="text"
                    value={ing.ingredient_type}
                    onChange={(e) => updateIngredient(idx, "ingredient_type", e.target.value)}
                    placeholder={t("Tip...")}
                  />
                ) : (
                  <span>{ing.ingredient_type || "-"}</span>
                )}
              </td>
              <td>
                <NumberInput
                  step="0.1"
                  value={ing.baker_percentage}
                  onValueChange={(v) => updateIngredient(idx, "baker_percentage", v)}
                  style={{ width: "80px" }}
                />
              </td>
              <td>{calculateMassKg(ing.baker_percentage)}</td>
              <td>
                <label className="toggle-label" style={{ margin: 0 }}>
                  <div className="toggle-switch small">
                    <input
                      type="checkbox"
                      checked={ing.is_new_ingredient}
                      onChange={() => toggleNewIngredient(idx)}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                </label>
              </td>
              <td>
                <button className="btn-sm btn-danger" onClick={() => removeIngredient(idx)}>
                  ✕
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <button className="btn-secondary" onClick={addIngredient} style={{ marginTop: "0.5rem" }}>
        {t("+ Dodaj sastojak")}
      </button>

      {/* Notes */}
      <div className="form-group" style={{ marginTop: "1rem" }}>
        <label>{t("Biljeske")}</label>
        <textarea
          value={newTrial.notes}
          onChange={(e) => setNewTrial({ ...newTrial, notes: e.target.value })}
          placeholder={t("Dodatne biljeske...")}
        />
      </div>
    </>
  );

  return (
    <div className="mes-page trials-page trial-create-page">
      <Header title={t("MES - Nova proba")} />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          <div className="mes-section trial-detail-header">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
              <div>
                <h3 style={{ margin: 0, border: "none", padding: 0 }}>{t("Nova proba")}</h3>
                <p style={{ margin: "0.25rem 0 0", color: "#888", fontSize: "0.9rem" }}>
                  {step === 1 ? t("Korak 1: Odaberite nacin kreiranja") : t("Korak 2: Definirajte pokus")}
                </p>
              </div>
              <button type="button" className="btn-secondary" onClick={() => history.push("/mes/trials")}>
                {t("← Natrag")}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="mes-section loading-state">{t("Ucitavanje...")}</div>
          ) : (
            <div className="mes-section">
              {step === 1 && renderModeSelection()}
              {step === 2 && renderTrialForm()}
              <div style={{ display: "flex", gap: "0.5rem", marginTop: "1.5rem", flexWrap: "wrap" }}>
                {step === 2 && (
                  <button type="button" className="btn-secondary" onClick={() => setStep(1)}>
                    {t("← Nazad")}
                  </button>
                )}
                <button type="button" className="btn-cancel" onClick={() => history.push("/mes/trials")}>
                  {t("Odustani")}
                </button>
                {step === 2 && (
                  <button type="button" className="btn-submit" onClick={handleCreateTrial} disabled={saving}>
                    {saving ? t("Spremanje...") : t("Stvori pokus")}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <ToastContainer autoClose={5000} position="bottom-right" hideProgressBar={false} theme="colored" />
    </div>
  );
};

export default TrialCreatePage;
